import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import SearchIcon from '../../assets/icons/search.svg?react';
import { PatientControllerApi } from '../../api/generated';
import type { PatientResponse } from '../../api/generated';
import { createApiConfiguration } from '../../api/apiConfig';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { showError } from '../../store/errorDialog/errorDialogSlice';
import { selectCanBookPatient } from '../../store/permissions/permissionsSlice';
import { Table } from '../UI';
import type { TableSortState } from '../UI';
import { Button } from '../UI/base/Button';
import { DeletePatientModal } from './DeletePatientModal';
import { getPatientColumns } from './patientColumns';
import { PatientDetailsDrawer } from '../PatientDetailsDrawer/PatientDetailsDrawer';
import { SectionHeader } from '../SectionHeader/SectionHeader';
import styles from './PatientsList.module.css';

export const PatientsList = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { locationId: locationIdParam } = useParams<{ locationId: string }>();
  const locationId = locationIdParam ?? null;
  const canBookPatient = useAppSelector(selectCanBookPatient);

  const [patients, setPatients] = useState<PatientResponse[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState<TableSortState | null>(null);
  const [patientToDelete, setPatientToDelete] = useState<PatientResponse | null>(null);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'view' | 'create'>('view');

  // Initial data fetch — re-runs whenever the URL location changes
  useEffect(() => {
    if (!locationId) return;
    const fetchPatients = async () => {
      try {
        const config = createApiConfiguration();
        const patientApi = new PatientControllerApi(config);
        const patientsResponse = await patientApi.listPatients({ locationId });
        setPatients(patientsResponse.data);
      } catch {
        dispatch(showError({ message: t('fetch-error') }));
      }
    };
    void fetchPatients();
  }, [locationId, dispatch, t]);

  // Re-fetches the patient list for a given location; returns the updated list or null on failure
  const refreshPatients = useCallback(
    async (locId: string): Promise<PatientResponse[] | null> => {
      try {
        const config = createApiConfiguration();
        const patientApi = new PatientControllerApi(config);
        const patientsResponse = await patientApi.listPatients({ locationId: locId });
        setPatients(patientsResponse.data);
        return patientsResponse.data;
      } catch {
        dispatch(showError({ message: t('fetch-error') }));
        return null;
      }
    },
    [dispatch, t]
  );

  // Derived list: excludes soft-deleted patients and applies name/CPR search filter
  const filteredPatients = useMemo(() => {
    let result = patients.filter((p) => !p.deleted);

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter((patient) => {
        const nameMatch = patient.name.toLowerCase().includes(query);
        const cprMatch = patient.cprNumber.toLowerCase().includes(query);
        return nameMatch || cprMatch;
      });
    }

    return result;
  }, [patients, searchQuery]);

  // Called after a patient is successfully deleted — clears the modal and refreshes the list
  const handleDeleted = useCallback(() => {
    setPatientToDelete(null);
    if (locationId) {
      void refreshPatients(locationId);
    }
  }, [locationId, refreshPatients]);

  const columns = useMemo(
    () =>
      getPatientColumns(t, {
        onViewDetails: (patient) => {
          setSelectedPatientId(patient.id);
          setDrawerMode('view');
          setIsDrawerOpen(true);
        },
        onDelete: (patient) => setPatientToDelete(patient),
        canDelete: canBookPatient
      }),
    [t, canBookPatient]
  );

  const handlePatientUpdated = useCallback(() => {
    if (locationId) void refreshPatients(locationId);
  }, [locationId, refreshPatients]);

  // Toggles direction when re-clicking the active column, otherwise sorts the
  // newly selected column ascending.
  const handleSortChange = useCallback((key: string) => {
    setSort((prev) =>
      prev?.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  }, []);

  return (
    <section>
      {/* Header: title + filters */}
      <SectionHeader title={t('patients-page-header')}>
        <div className={styles.filters}>
          {/* Search input — filters by patient name or CPR number */}
          {/* TODO: replace custom search input with InputFieldString from qampo-ui-library */}
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>{t('settings-search-patient')}</label>
            <div className={styles.searchInputWrapper}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                value={searchQuery}
                placeholder={t('settings-search-placeholder')}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </SectionHeader>

      {/* Patients table */}
      <Table
        columns={columns}
        data={filteredPatients}
        sort={sort}
        onSortChange={handleSortChange}
        getRowKey={(patient) => patient.id}
        footer={
          canBookPatient ? (
            <Button
              type="tertiary"
              onClick={() => {
                setSelectedPatientId(null);
                setDrawerMode('create');
                setIsDrawerOpen(true);
              }}
              styles={{ root: { fontSize: 'var(--typography-size-text-label,12px)' } }}
            >
              {t('add-patient')}
            </Button>
          ) : undefined
        }
      />

      {/* Delete confirmation modal — shown when a patient is selected for deletion */}
      {patientToDelete && locationId && (
        <DeletePatientModal
          patient={patientToDelete}
          locationId={locationId}
          onClose={() => setPatientToDelete(null)}
          onDeleted={handleDeleted}
        />
      )}

      {/* Patient details / create drawer */}
      <PatientDetailsDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        patientId={selectedPatientId}
        locationId={locationId}
        mode={drawerMode}
        onPatientUpdated={handlePatientUpdated}
      />
    </section>
  );
};
